import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { SERVER_ENDPOINT } from '../shared/websocket.token';

const VB_LXP_AUTH_TOKEN_STORAGE_KEY = 'vb_lxp_auth_token';

interface MvLoginRequest {
	username: string;
	password: string;
}

interface MvLoginResponse {
	token: string;
}

interface TokenPayload {
	username: string;
	role: string;
	exp: number;
	iat: number;
}

@Injectable({
	providedIn: 'root',
})
export class VbLxpAuthService {
	constructor(
		private readonly http: HttpClient,
		@Inject(SERVER_ENDPOINT) private readonly serverEndpoint: string,
	) { }

	login(loginPath: string, username: string, password: string): Observable<MvLoginResponse> {
		const payload: MvLoginRequest = { username, password };
		const normalizedPath = loginPath.replace(/^\//, '');
		return this.http.post<MvLoginResponse>(`${this.serverEndpoint}${normalizedPath}`, payload);
	}

	getToken(): string | null {
		return localStorage.getItem(VB_LXP_AUTH_TOKEN_STORAGE_KEY);
	}

	saveToken(token: string): void {
		localStorage.setItem(VB_LXP_AUTH_TOKEN_STORAGE_KEY, token);
	}

	clearToken(): void {
		localStorage.removeItem(VB_LXP_AUTH_TOKEN_STORAGE_KEY);
	}

	/**
	 * Decode JWT token and extract payload
	 */
	private decodeToken(token: string): TokenPayload | null {
		try {
			const parts = token.split('.');
			if (parts.length !== 3) {
				return null;
			}
			const payload = parts[1];
			const decoded = JSON.parse(atob(payload));
			return decoded;
		} catch (error) {
			console.error('Error decoding token:', error);
			return null;
		}
	}

	/**
	 * Get current user's role from token
	 */
	getUserRole(): string | null {
		const token = this.getToken();
		if (!token) {
			return null;
		}
		const payload = this.decodeToken(token);
		return payload?.role || null;
	}

	/**
	 * Check if current user has admin role
	 */
	isAdmin(): boolean {
		return this.getUserRole() === 'admin';
	}

	/**
	 * Check if current user is authenticated
	 */
	isAuthenticated(): boolean {
		const token = this.getToken();
		if (!token) {
			return false;
		}
		const payload = this.decodeToken(token);
		if (!payload) {
			return false;
		}
		// Check if token is expired
		const now = Math.floor(Date.now() / 1000);
		return payload.exp > now;
	}
}
